import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { SentryService } from '@rd-lib/sentry/sentry.service';
import { AppInit } from '@rd/app-init/app-init.action';
import { ChangeCurrency } from '@rd/core/currency/store/actions/currency.actions';
import { ChangeLoyaltyCurrency } from '@rd/core/loyalty-currency/store/actions/loyalty-currency.actions';
import { DEFAULT_CURRENCY_CODE } from '@rd/core/tokens/rd-lib.token';
import { NydusNetworkDataError } from '@rd/errors/nydus-network-data-error';
import { BootstrapData, HttpError } from '@rd/types';
import { isNotNull } from '@rd/utils/isNotNull';
import { of, timer } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { BootstrapApiService } from '../../../services/bootstrap-api/bootstrap-api.service';
import * as actions from '../actions/tenant.actions';

@Injectable()
export class TenantEffects {
  constructor(
    private actions$: Actions,
    private bootstrapApiService: BootstrapApiService,
    private sentryService: SentryService,
    @Inject(DEFAULT_CURRENCY_CODE) private defaultCurrencyCode: string,
  ) {}

  turnOffPageLoadingState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.TenantActionTypes.SET_PAGE_LOADING_STATE),
      switchMap(() =>
        timer(5_000).pipe(
          take(1),
          map(() => new actions.SetPageLoadingState({ isLoading: false })),
        ),
      ),
    ),
  );

  initLoadBootstrap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppInit),
      map(() => new actions.LoadBootstrap()),
    ),
  );

  loadBootstrap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.TenantActionTypes.LOAD_BOOTSTRAP),
      switchMap(() => {
        return this.bootstrapApiService.getBootstrap().pipe(
          map((bootstrapData: BootstrapData) => {
            this.checkBootstrapDataIntegrity(bootstrapData);
            return new actions.LoadBootstrapSuccess(bootstrapData);
          }),
          catchError((error: HttpError) => {
            this.reportBootstrapFail(error);
            return of(new actions.LoadBootstrapFail(error));
          }),
        );
      }),
    ),
  );

  setDefaultCurrency$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.TenantActionTypes.LOAD_BOOTSTRAP_SUCCESS),
      map((action: actions.LoadBootstrapSuccess) => {
        const { currencies } = action.payload;
        return currencies.find(
          (currency) => currency.code === this.defaultCurrencyCode,
        );
      }),
      tap((defaultCurrency) => {
        if (!defaultCurrency) {
          this.sentryService.manualError(
            'Tenant Default Currency is not found.',
          );
        }
      }),
      filter(isNotNull),
      map((defaultCurrency) => new ChangeCurrency(defaultCurrency)),
    ),
  );

  setDefaultLoyalCurrency$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.TenantActionTypes.LOAD_BOOTSTRAP_SUCCESS),
      map(
        ({ payload: { loyaltyCurrencies } }: actions.LoadBootstrapSuccess) =>
          new ChangeLoyaltyCurrency(loyaltyCurrencies[0]),
      ),
    ),
  );

  private checkBootstrapDataIntegrity(bootstrapData: BootstrapData): void {
    if (bootstrapData.locales.length === 0) {
      const nydusNetworkDataError = new NydusNetworkDataError({
        httpMethod: 'GET',
        endpoint: '/bootstrap',
        message: 'Locales are empty.',
      });

      this.sentryService.manualError(nydusNetworkDataError.toString());
    }

    if (bootstrapData.currencies.length === 0) {
      const nydusNetworkDataError = new NydusNetworkDataError({
        httpMethod: 'GET',
        endpoint: '/bootstrap',
        message: 'Currencies are empty.',
      });

      this.sentryService.manualError(nydusNetworkDataError.toString());
    }

    if (bootstrapData.loyaltyCurrencies.length === 0) {
      const nydusNetworkDataError = new NydusNetworkDataError({
        httpMethod: 'GET',
        endpoint: '/bootstrap',
        message: 'LoyaltyCurrencies are empty.',
      });

      this.sentryService.manualError(nydusNetworkDataError.toString());
    }
  }

  private reportBootstrapFail(error: HttpError): void {
    const nydusNetworkDataError = new NydusNetworkDataError({
      httpMethod: 'GET',
      endpoint: '/bootstrap',
      message: `HTTPError: ${error.status} - ${error.message}`,
    });

    // Status 0 means the XHR never completed — the browser aborted it, so
    // there is no response to report on. /bootstrap is the first request of
    // a cold load, so on mobile WebViews this is overwhelmingly a user who
    // backgrounded the app or navigated away mid-request, plus ordinary
    // mobile-network drops. Not actionable per user, but a spike still
    // signals a real NN/CORS outage — so drop it only when the page is
    // provably gone or the device is offline, and downgrade the rest to a
    // warning rather than silencing them.
    if (error.status === 0 && this.isClientGone()) {
      return;
    }

    if (error.status === 0) {
      this.sentryService.manualWarning(nydusNetworkDataError.toString());
      return;
    }

    this.sentryService.manualError(nydusNetworkDataError.toString());
  }

  private isClientGone(): boolean {
    if (
      typeof document !== 'undefined' &&
      document.visibilityState === 'hidden'
    )
      return true;

    return typeof navigator !== 'undefined' && navigator.onLine === false;
  }
}
